
import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { db } from "@/services/firebaseService";
import { Organization } from "@/types/organization";
import { getCampOrganizations } from "@/services/campOrganizationQuery";
import { useAuth } from "@/context/AuthContext";
import { tokenCoordinator } from "@/services/tokenCoordinationService";

export function useOrganizationData() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const lastRetryAttempt = useRef(0);
  const MIN_RETRY_INTERVAL = 30 * 1000; // 30 seconds between retry attempts

  // Enhanced queryFn with token refresh and retry logic
  const queryFn = async () => {
    console.log("useOrganizationData: Fetching organizations from campOrganizations collection");
    try {
      const organizations = await getCampOrganizations(db);
      console.log(`useOrganizationData: Successfully fetched ${organizations.length} organizations:`, 
        organizations.map(org => ({ id: org.id, name: org.name })));
      
      // Reset retry count on success
      setRetryCount(0);
      return organizations;
    } catch (error: any) {
      console.error("useOrganizationData: Error getting organizations:", error);
      
      // Handle permission denied errors specifically
      if (error.code === 'permission-denied' && retryCount < maxRetries) {
        const now = Date.now();
        const timeSinceLastRetry = now - lastRetryAttempt.current;
        
        // Prevent too frequent retry attempts
        if (timeSinceLastRetry < MIN_RETRY_INTERVAL) {
          console.log(`useOrganizationData: Too soon since last retry (${timeSinceLastRetry}ms), throwing error`);
          throw error;
        }
        
        console.log(`useOrganizationData: Permission denied, attempting token refresh (attempt ${retryCount + 1}/${maxRetries})`);
        lastRetryAttempt.current = now;
        
        // Attempt coordinated token refresh
        const refreshSuccess = await tokenCoordinator.coordinatedTokenRefresh(currentUser);
        
        if (refreshSuccess) {
          console.log('useOrganizationData: Token refresh successful, incrementing retry count and retrying');
          setRetryCount(prev => prev + 1);
          
          // Wait a bit before retrying to allow tokens to propagate
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Simply retry the original request - don't invalidate the query
          // because we're already inside the queryFn and returning fresh data
          return getCampOrganizations(db);
        } else {
          console.error('useOrganizationData: Token refresh failed, throwing original error');
        }
      } else if (error.code === 'permission-denied' && retryCount >= maxRetries) {
        console.error(`useOrganizationData: Max retries (${maxRetries}) reached for permission-denied error`);
      }
      
      // Handle App Check throttling errors
      if (error.message?.includes('throttled') || error.code === 'appCheck/throttled') {
        console.error('useOrganizationData: App Check throttled, will not retry immediately');
        // Don't increment retry count for throttling - let the monitoring service handle it
      }
      
      throw error;
    }
  };

  const { 
    data: organizations, 
    isLoading, 
    error 
  } = useQuery<Organization[]>({
    queryKey: ['organizations'], // Remove retryCount to prevent cache fragmentation
    queryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: false, // Disable automatic retry, we handle it manually
    retryOnMount: false, // Don't retry on component mount
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid triggering more errors
  });

  // Enhanced error logging with more context
  if (error) {
    console.error("useOrganizationData: Error in query:", {
      error,
      retryCount,
      maxRetries,
      timeSinceLastRetry: Date.now() - lastRetryAttempt.current,
      userAuthenticated: !!currentUser,
      errorCode: (error as any)?.code,
      errorMessage: (error as any)?.message
    });
  }

  // Function to manually refresh the organizations data
  const refreshOrganizationData = () => {
    console.log("useOrganizationData: Manually invalidating organizations query");
    setRetryCount(0); // Reset retry count on manual refresh
    return queryClient.invalidateQueries({ queryKey: ['organizations'] });
  };

  // Function to force a token refresh and retry
  const refreshTokensAndRetry = async () => {
    console.log("useOrganizationData: Forcing token refresh and retry");
    const refreshSuccess = await tokenCoordinator.coordinatedTokenRefresh(currentUser);
    
    if (refreshSuccess) {
      setRetryCount(0);
      return queryClient.invalidateQueries({ queryKey: ['organizations'] });
    } else {
      console.error("useOrganizationData: Token refresh failed during manual retry");
      return false;
    }
  };

  return {
    organizations: organizations || [],
    isLoading,
    error,
    refreshOrganizationData,
    refreshTokensAndRetry,
    retryCount,
    maxRetries,
    canRetry: retryCount < maxRetries
  };
}
